import { useAppStore } from '@store'
import { LinkedItem, Material, Textures, Zone } from '@types'
import isLinked from '@utils/sceneObjects/assertions/isLinked'

/** Activar consola: VITE_FREE_DIM_DEBUG_MATERIAL=true en .env.local */
export const FREE_DIM_MATERIAL_DEBUG =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_FREE_DIM_DEBUG_MATERIAL === 'true'

const PUERTA_ZONE_NAMES = new Set(['puerta', 'door'])

function zoneLabel(texture?: Textures[number], zoneName?: string): string {
  return (texture?.zoneDisplayName ?? texture?.zone ?? zoneName ?? '').toLowerCase()
}

export function isZocaloZone(
  zone: Zone[string],
  texture?: Textures[number],
): boolean {
  const display = zoneLabel(texture, zone.name)
  if (display.includes('zocalo') || display.includes('zócalo')) return true
  const mat = zone.material as Material & { acabado?: string }
  return Boolean(mat?.acabado?.toLowerCase().includes('zocalo'))
}

export type LinkedZoneMaterialSnapshot = {
  zoneKey: string
  meshName: string
  zoneDisplayName: string | undefined
  materialName: string | undefined
  url: string | undefined
  color: string | undefined
  roughness: number | undefined
  metalness: number | undefined
  envMapIntensity: number | undefined
  bumpScale: number | undefined
  repeatX: number | undefined
  repeatY: number | undefined
  opacity: number | undefined
}

export type LinkedItemMaterialReport = {
  id: string
  catalogID: string
  description: string
  hasMorphs: boolean
  usesFreeDimension: boolean
  zones: LinkedZoneMaterialSnapshot[]
}

function snapshotZone(
  zoneKey: string,
  zone: Zone[string],
  textures: LinkedItem['textures'],
): LinkedZoneMaterialSnapshot {
  const tex = textures?.find((t) => t.zoneName === zone.name || t.zoneName === zoneKey)
  const m = zone.material
  return {
    zoneKey,
    meshName: zone.name ?? zoneKey,
    zoneDisplayName: tex?.zoneDisplayName ?? tex?.zone,
    materialName: m?.name,
    url: m?.url,
    color: m?.color,
    roughness: m?.roughness,
    metalness: m?.metalness,
    envMapIntensity: m?.envMapIntensity,
    bumpScale: m?.bumpScale,
    repeatX: m?.repeatX,
    repeatY: m?.repeatY,
    opacity: m?.opacity,
  }
}

export function getLinkedItemMaterialReport(itemId: string): LinkedItemMaterialReport | null {
  const item = useAppStore.getState().items.get(itemId as never)
  if (!item || !isLinked(item) || item.type !== 'linkedItem') return null

  const linked = item as LinkedItem
  const zones = Object.entries(linked.zones ?? {}).map(([zoneKey, zone]) =>
    snapshotZone(zoneKey, zone, linked.textures),
  )

  return {
    id: linked.id,
    catalogID: String(linked.catalogID),
    description: linked.description ?? '',
    hasMorphs: Boolean(linked.hasMorphs),
    usesFreeDimension: !linked.hasMorphs,
    zones,
  }
}

function isPuertaZone(
  zone: Zone[string],
  texture?: Textures[number],
): boolean {
  const display = (texture?.zoneDisplayName ?? texture?.zone ?? '').toLowerCase()
  const zoneField = (texture?.zone ?? '').toLowerCase()
  if (PUERTA_ZONE_NAMES.has(display) || PUERTA_ZONE_NAMES.has(zoneField)) return true
  const mat = zone.material as Material & { acabado?: string; modelo?: string }
  if (mat?.acabado?.toLowerCase().includes('puerta')) return true
  return false
}

function findPuertaZoneEntry(item: LinkedItem): {
  zoneKey: string
  zone: Zone[string]
  texture?: Textures[number]
} | null {
  const zones = item.zones ?? {}
  const textures = item.textures ?? []

  for (const [zoneKey, zone] of Object.entries(zones)) {
    const tex =
      textures.find((t) => t.zoneName === zone.name) ??
      textures.find((t) => t.zoneName === zoneKey)
    if (isPuertaZone(zone, tex)) {
      return { zoneKey, zone, texture: tex }
    }
  }

  const texPuerta = textures.find(
    (t) =>
      PUERTA_ZONE_NAMES.has((t.zoneDisplayName ?? '').toLowerCase()) ||
      PUERTA_ZONE_NAMES.has(String(t.zone ?? '').toLowerCase()),
  )
  if (texPuerta) {
    const zoneKey =
      Object.keys(zones).find(
        (k) => zones[k].name === texPuerta.zoneName || k === texPuerta.zoneName,
      ) ?? texPuerta.zoneName
    if (zoneKey && zones[zoneKey]) {
      return { zoneKey, zone: zones[zoneKey], texture: texPuerta }
    }
  }

  return null
}

export type PuertaMaterialReport = {
  itemId: string
  catalogID: string
  description: string
  hasMorphs: boolean
  usesFreeDimension: boolean
  zoneKey: string
  meshName: string
  /** Material completo en store (zones.*.material) — lo que usa el visor 3D */
  zoneMaterial: Material & Record<string, unknown>
  /** Entrada en item.textures para la misma zona */
  textureEntry: Textures[number] | null
  idAcabadoUnidadModeloAcabado: string | undefined
}

/** Material Puerta completo de un linkedItem (consola + objeto devuelto). */
export function logPuertaMaterial(itemId: string): PuertaMaterialReport | null {
  const item = useAppStore.getState().items.get(itemId as never)
  if (!item || !isLinked(item) || item.type !== 'linkedItem') {
    if (FREE_DIM_MATERIAL_DEBUG) {
      console.warn('[logPuertaMaterial] No es linkedItem válido:', itemId)
    }
    return null
  }

  const linked = item as LinkedItem
  const puerta = findPuertaZoneEntry(linked)
  if (!puerta) {
    if (FREE_DIM_MATERIAL_DEBUG) {
      console.warn(
        `[logPuertaMaterial] ${linked.catalogID}: no hay zona Puerta. Zonas:`,
        Object.entries(linked.zones ?? {}).map(([k, z]) => ({
          key: k,
          mesh: z.name,
          texture: linked.textures?.find((t) => t.zoneName === z.name)?.zoneDisplayName,
        })),
      )
    }
    return null
  }

  const report: PuertaMaterialReport = {
    itemId: linked.id,
    catalogID: String(linked.catalogID),
    description: linked.description ?? '',
    hasMorphs: Boolean(linked.hasMorphs),
    usesFreeDimension: !linked.hasMorphs,
    zoneKey: puerta.zoneKey,
    meshName: puerta.zone.name ?? puerta.zoneKey,
    zoneMaterial: { ...(puerta.zone.material ?? {}) } as Material & Record<string, unknown>,
    textureEntry: puerta.texture ? { ...puerta.texture } : null,
    idAcabadoUnidadModeloAcabado: puerta.zone.idAcabadoUnidadModeloAcabado,
  }

  if (FREE_DIM_MATERIAL_DEBUG) {
    console.group(`[FreeDim material Puerta] ${report.catalogID} — ${report.description}`)
    console.log('Resumen', {
      itemId: report.itemId,
      zoneKey: report.zoneKey,
      meshName: report.meshName,
      idAcabadoUnidadModeloAcabado: report.idAcabadoUnidadModeloAcabado,
      hasMorphs: report.hasMorphs,
      usesFreeDimension: report.usesFreeDimension,
    })
    console.log('zones[puerta].material (completo — fuente del visor)', report.zoneMaterial)
    if (report.textureEntry) {
      console.log('textures[] (misma zona)', report.textureEntry)
    } else {
      console.warn('Sin entrada en textures[] para Puerta')
    }
    console.groupEnd()
  }

  return report
}

/**
 * Por código de catálogo (RB805, RC2305, KBB8030…).
 * Si hay varias en escena, lista todas.
 */
export function logPuertaMaterialByCatalog(...catalogCodes: string[]): PuertaMaterialReport[] {
  const codes = catalogCodes.map((c) => c.trim().toUpperCase())
  const items = [...useAppStore.getState().items.values()].filter(
    (i) =>
      isLinked(i) &&
      i.type === 'linkedItem' &&
      codes.includes(String(i.catalogID).trim().toUpperCase()),
  ) as LinkedItem[]

  if (items.length === 0) {
    if (FREE_DIM_MATERIAL_DEBUG) {
      console.warn('[logPuertaMaterialByCatalog] No hay items en escena para:', catalogCodes)
    }
    return []
  }

  if (FREE_DIM_MATERIAL_DEBUG) {
    console.group(`[FreeDim material Puerta] compare ${codes.join(', ')}`)
  }
  const reports = items.map((i) => logPuertaMaterial(i.id)).filter((r): r is PuertaMaterialReport => r != null)

  if (FREE_DIM_MATERIAL_DEBUG && reports.length >= 2) {
    const base = reports[0].zoneMaterial
    reports.slice(1).forEach((r) => {
      const diff: Record<string, { first: unknown; other: unknown }> = {}
      const allKeys = new Set([
        ...Object.keys(base),
        ...Object.keys(r.zoneMaterial),
      ]) as Set<string>
      allKeys.forEach((key) => {
        const a = base[key as keyof Material]
        const b = r.zoneMaterial[key as keyof Material]
        if (a !== b) {
          diff[key] = { first: a, other: b }
        }
      })
      console.log(
        `Diferencias Puerta.material: ${reports[0].catalogID} vs ${r.catalogID}`,
        diff,
      )
    })
  }
  if (FREE_DIM_MATERIAL_DEBUG) {
    console.groupEnd()
  }
  return reports
}

export type ZocaloMaterialReport = {
  itemId: string
  catalogID: string
  description: string
  hasMorphs: boolean
  usesFreeDimension: boolean
  zoneKey: string
  meshName: string
  zoneMaterial: Material & Record<string, unknown>
  textureEntry: Textures[number] | null
  idAcabadoUnidadModeloAcabado: string | undefined
}

export type ZocaloRenderRuntimeContext = {
  phase: string
  meshName: string
  /** MeshLambertMaterial en free-dim (useGLBInstance) vs MeshStandardMaterial en morphs normales */
  threeMaterialType?: string
  threeRoughness?: number
  threeMetalness?: number
  threeEnvMapIntensity?: number
  mapUrl?: string | null
  mapRepeatX?: number
  mapRepeatY?: number
  mapOffsetU?: number
  mapOffsetV?: number
  stableLinkedZoneTextures?: boolean
  freeDimensionScaleCompX?: number
  itemWidth?: number
  itemHeight?: number
  storeRepeatX?: number
  storeRepeatY?: number
  storeRoughness?: number
  storeMetalness?: number
}

function findZocaloZoneEntry(item: LinkedItem): {
  zoneKey: string
  zone: Zone[string]
  texture?: Textures[number]
} | null {
  const zones = item.zones ?? {}
  const textures = item.textures ?? []

  for (const [zoneKey, zone] of Object.entries(zones)) {
    const tex =
      textures.find((t) => t.zoneName === zone.name) ??
      textures.find((t) => t.zoneName === zoneKey)
    if (isZocaloZone(zone, tex)) {
      return { zoneKey, zone, texture: tex }
    }
  }

  const texZocalo = textures.find(
    (t) =>
      zoneLabel(t).includes('zocalo') || zoneLabel(t).includes('zócalo'),
  )
  if (texZocalo) {
    const zoneKey =
      Object.keys(zones).find(
        (k) => zones[k].name === texZocalo.zoneName || k === texZocalo.zoneName,
      ) ?? texZocalo.zoneName
    if (zoneKey && zones[zoneKey]) {
      return { zoneKey, zone: zones[zoneKey], texture: texZocalo }
    }
  }

  return null
}

/** Material Zócalo en store + contexto de render (consola). Filtrar: `[FreeDim material Zocalo]` */
export function logZocaloMaterial(
  itemId: string,
  runtime?: ZocaloRenderRuntimeContext,
): ZocaloMaterialReport | null {
  const item = useAppStore.getState().items.get(itemId as never)
  if (!item || !isLinked(item) || item.type !== 'linkedItem') {
    if (FREE_DIM_MATERIAL_DEBUG) {
      console.warn('[logZocaloMaterial] No es linkedItem válido:', itemId)
    }
    return null
  }

  const linked = item as LinkedItem
  const zocalo = findZocaloZoneEntry(linked)
  if (!zocalo) {
    if (FREE_DIM_MATERIAL_DEBUG) {
      console.warn(
        `[logZocaloMaterial] ${linked.catalogID}: no hay zona Zócalo. Zonas:`,
        Object.entries(linked.zones ?? {}).map(([k, z]) => ({
          key: k,
          mesh: z.name,
          texture: linked.textures?.find((t) => t.zoneName === z.name)?.zoneDisplayName,
        })),
      )
    }
    return null
  }

  const report: ZocaloMaterialReport = {
    itemId: linked.id,
    catalogID: String(linked.catalogID),
    description: linked.description ?? '',
    hasMorphs: Boolean(linked.hasMorphs),
    usesFreeDimension: !linked.hasMorphs,
    zoneKey: zocalo.zoneKey,
    meshName: zocalo.zone.name ?? zocalo.zoneKey,
    zoneMaterial: { ...(zocalo.zone.material ?? {}) } as Material & Record<string, unknown>,
    textureEntry: zocalo.texture ? { ...zocalo.texture } : null,
    idAcabadoUnidadModeloAcabado: zocalo.zone.idAcabadoUnidadModeloAcabado,
  }

  if (FREE_DIM_MATERIAL_DEBUG) {
    console.group(`[FreeDim material Zocalo] ${report.catalogID} — ${report.description}`)
    console.log('Resumen store', {
      itemId: report.itemId,
      zoneKey: report.zoneKey,
      meshName: report.meshName,
      idAcabadoUnidadModeloAcabado: report.idAcabadoUnidadModeloAcabado,
      hasMorphs: report.hasMorphs,
      usesFreeDimension: report.usesFreeDimension,
      /** Si true, useGLBInstance usa MeshLambertMaterial (aspecto más plano que Standard) */
      likelyLambertInViewer: report.usesFreeDimension,
    })
    console.log('zones[zocalo].material', {
      name: report.zoneMaterial.name,
      url: report.zoneMaterial.url,
      color: report.zoneMaterial.color,
      roughness: report.zoneMaterial.roughness,
      metalness: report.zoneMaterial.metalness,
      envMapIntensity: report.zoneMaterial.envMapIntensity,
      repeatX: report.zoneMaterial.repeatX,
      repeatY: report.zoneMaterial.repeatY,
      bumpScale: report.zoneMaterial.bumpScale,
      opacity: report.zoneMaterial.opacity,
    })
    if (report.textureEntry) {
      console.log('textures[] zócalo', {
        zone: report.textureEntry.zone,
        zoneDisplayName: report.textureEntry.zoneDisplayName,
        url: report.textureEntry.url,
        repeatX: report.textureEntry.repeatX,
        repeatY: report.textureEntry.repeatY,
        linkedTextureID: report.textureEntry.linkedTextureID,
      })
    } else {
      console.warn('Sin entrada en textures[] para Zócalo')
    }
    if (runtime) {
      console.log('Runtime visor 3D', runtime)
      const storeR = report.zoneMaterial.roughness
      const storeM = report.zoneMaterial.metalness
      if (
        runtime.threeMaterialType === 'MeshLambertMaterial' &&
        (storeM != null && storeM > 0)
      ) {
        console.warn(
          '[Zocalo] MeshLambertMaterial no usa metalness PBR: el zócalo puede verse distinto aunque el acabado sea el mismo.',
        )
      }
      if (
        runtime.mapRepeatX != null &&
        runtime.storeRepeatX != null &&
        Math.abs(runtime.mapRepeatX - runtime.storeRepeatX) > 0.02
      ) {
        console.warn('[Zocalo] repeat del mapa ≠ repeat en store (p. ej. freeDimensionScaleCompX)', {
          mapRepeatX: runtime.mapRepeatX,
          storeRepeatX: runtime.storeRepeatX,
          freeDimensionScaleCompX: runtime.freeDimensionScaleCompX,
        })
      }
    }
    console.groupEnd()
  }

  return report
}

/** Compara zócalo entre catálogos en escena (p. ej. RB805 vs KBB8030). */
export function logZocaloMaterialByCatalog(...catalogCodes: string[]): ZocaloMaterialReport[] {
  const codes = catalogCodes.map((c) => c.trim().toUpperCase())
  const items = [...useAppStore.getState().items.values()].filter(
    (i) =>
      isLinked(i) &&
      i.type === 'linkedItem' &&
      codes.includes(String(i.catalogID).trim().toUpperCase()),
  ) as LinkedItem[]

  if (items.length === 0) {
    if (FREE_DIM_MATERIAL_DEBUG) {
      console.warn('[logZocaloMaterialByCatalog] No hay items en escena para:', catalogCodes)
    }
    return []
  }

  if (FREE_DIM_MATERIAL_DEBUG) {
    console.group(`[FreeDim material Zocalo] compare ${codes.join(', ')}`)
  }
  const reports = items
    .map((i) => logZocaloMaterial(i.id))
    .filter((r): r is ZocaloMaterialReport => r != null)

  if (FREE_DIM_MATERIAL_DEBUG && reports.length >= 2) {
    const base = reports[0].zoneMaterial
    reports.slice(1).forEach((r) => {
      const diff: Record<string, { first: unknown; other: unknown }> = {}
      const allKeys = new Set([...Object.keys(base), ...Object.keys(r.zoneMaterial)])
      allKeys.forEach((key) => {
        const a = base[key as keyof Material]
        const b = r.zoneMaterial[key as keyof Material]
        if (a !== b) diff[key] = { first: a, other: b }
      })
      console.log(`Diferencias zones[zocalo].material: ${reports[0].catalogID} vs ${r.catalogID}`, diff)
      console.log('usesFreeDimension', {
        [reports[0].catalogID]: reports[0].usesFreeDimension,
        [r.catalogID]: r.usesFreeDimension,
      })
    })
  }
  if (FREE_DIM_MATERIAL_DEBUG) {
    console.groupEnd()
  }
  return reports
}

/** Compara materiales de zonas entre varios linkedItem (consola). */
export function compareLinkedItemMaterials(...itemIds: string[]): void {
  const reports = itemIds
    .map(getLinkedItemMaterialReport)
    .filter((r): r is LinkedItemMaterialReport => r != null)

  if (reports.length === 0) {
    if (FREE_DIM_MATERIAL_DEBUG) {
      console.warn('[compareLinkedItemMaterials] No hay items válidos para:', itemIds)
    }
    return
  }

  if (!FREE_DIM_MATERIAL_DEBUG) return

  console.group('[compareLinkedItemMaterials] zones.material (store)')
  reports.forEach((r) => {
    console.group(`${r.catalogID} — ${r.description} (${r.id})`)
    console.log({
      hasMorphs: r.hasMorphs,
      usesFreeDimension: r.usesFreeDimension,
    })
    console.table(r.zones)
    console.groupEnd()
  })

  if (reports.length >= 2) {
    const [a, b] = reports
    const puertaA = a.zones.find((z) => z.zoneDisplayName === 'Puerta') ?? a.zones[0]
    const puertaB = b.zones.find((z) => z.zoneDisplayName === 'Puerta') ?? b.zones[0]
    if (puertaA && puertaB) {
      const keys: (keyof LinkedZoneMaterialSnapshot)[] = [
        'materialName',
        'url',
        'color',
        'roughness',
        'metalness',
        'envMapIntensity',
        'bumpScale',
        'repeatX',
        'repeatY',
      ]
      const diff: Record<string, { a: unknown; b: unknown }> = {}
      keys.forEach((key) => {
        if (puertaA[key] !== puertaB[key]) {
          diff[key] = { a: puertaA[key], b: puertaB[key] }
        }
      })
      console.log(`Diferencias Puerta: ${a.catalogID} vs ${b.catalogID}`, diff)
    }
  }
  console.groupEnd()
}

/** Auto-registro en consola (Vite DEV). */
function registerMaterialDebugOnWindow(): void {
  if (typeof window === 'undefined') return
  const w = window as Window & {
    compareLinkedItemMaterials?: typeof compareLinkedItemMaterials
    logPuertaMaterial?: typeof logPuertaMaterial
    logPuertaMaterialByCatalog?: typeof logPuertaMaterialByCatalog
    logZocaloMaterial?: typeof logZocaloMaterial
    logZocaloMaterialByCatalog?: typeof logZocaloMaterialByCatalog
  }
  w.compareLinkedItemMaterials = compareLinkedItemMaterials
  w.logPuertaMaterial = logPuertaMaterial
  w.logPuertaMaterialByCatalog = logPuertaMaterialByCatalog
  w.logZocaloMaterial = logZocaloMaterial
  w.logZocaloMaterialByCatalog = logZocaloMaterialByCatalog
}

const isDev =
  (typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV)) ||
  (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development')

if (isDev) {
  registerMaterialDebugOnWindow()
}
